<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Thumbs, Navigation, Keyboard, Zoom } from 'swiper/modules'
import type { Swiper as SwiperClass } from 'swiper/types'
import { imageUrlWithProxy } from '~/utils/imageproxy'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'
import 'swiper/css/zoom'

const props = defineProps<{
  images: Array<{ url: string; description?: string }>
  startIndex?: number
}>()

const visible = defineModel<boolean>('visible', { default: false })

const thumbsSwiper = ref<SwiperClass | null>(null)
const mainSwiper = ref<SwiperClass | null>(null)
const activeIndex = ref(props.startIndex ?? 0)

const swiperModules = [Thumbs, Navigation, Keyboard, Zoom]
const thumbsModules = [Navigation]

function onMainReady(swiper: SwiperClass) {
  mainSwiper.value = swiper
}

function onThumbsReady(swiper: SwiperClass) {
  thumbsSwiper.value = swiper
}

function onSlideChange(swiper: SwiperClass) {
  activeIndex.value = swiper.activeIndex
}

function close() {
  // Destroy swiper instances before hiding to clean up global listeners
  if (mainSwiper.value && !mainSwiper.value.destroyed) {
    mainSwiper.value.destroy(true, true)
    mainSwiper.value = null
  }
  if (thumbsSwiper.value && !thumbsSwiper.value.destroyed) {
    thumbsSwiper.value.destroy(true, true)
    thumbsSwiper.value = null
  }
  visible.value = false
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') close()
}

watch(visible, (val) => {
  if (val) {
    activeIndex.value = props.startIndex ?? 0
    // Reset swiper refs for fresh instances
    thumbsSwiper.value = null
    mainSwiper.value = null
    document.addEventListener('keydown', onKeydown)
    document.body.style.overflow = 'hidden'
  } else {
    document.removeEventListener('keydown', onKeydown)
    document.body.style.overflow = ''
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="lightbox-overlay" @click.self="close">
      <button class="lightbox-close" aria-label="Close" @click="close">
        <span class="icon icon-close"></span>
      </button>

      <div class="lightbox-counter text-caption">
        {{ activeIndex + 1 }} / {{ images.length }}
      </div>

      <div class="lightbox-content" @click.stop>
        <Swiper
          class="lightbox-main-swiper"
          :modules="swiperModules"
          :initial-slide="startIndex ?? 0"
          :slides-per-view="1"
          :space-between="0"
          :keyboard="{ enabled: true }"
          :zoom="true"
          :navigation="true"
          :thumbs="{ swiper: thumbsSwiper }"
          @swiper="onMainReady"
          @slide-change="onSlideChange"
        >
          <SwiperSlide v-for="(img, idx) in images" :key="idx">
            <div class="swiper-zoom-container">
              <img
                :src="imageUrlWithProxy(img.url, 1200, 800)"
                :alt="img.description || ''"
                loading="lazy"
              />
            </div>
          </SwiperSlide>
        </Swiper>

        <Swiper
          class="lightbox-thumbs-swiper"
          :modules="thumbsModules"
          :slides-per-view="'auto'"
          :space-between="8"
          :watch-slides-progress="true"
          :free-mode="true"
          @swiper="onThumbsReady"
        >
          <SwiperSlide v-for="(img, idx) in images" :key="idx">
            <img
              :src="imageUrlWithProxy(img.url, 100, 100)"
              :alt="img.description || ''"
              loading="lazy"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.lightbox-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.lightbox-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 28px;
  cursor: pointer;
  padding: 8px;
  line-height: 1;

  &:hover {
    opacity: 0.8;
  }
}

.lightbox-counter {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  color: #fff;
  font-size: 14px;
  background: rgba(0, 0, 0, 0.5);
  padding: 4px 14px;
  border-radius: 12px;
}

.lightbox-content {
  width: 100%;
  max-width: 1200px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 60px 16px 16px;
  box-sizing: border-box;
}

.lightbox-main-swiper {
  width: 100%;
  flex: 1;
  min-height: 0;

  :deep(.swiper-slide) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.swiper-zoom-container) {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 4px;
  }

  :deep(.swiper-button-prev),
  :deep(.swiper-button-next) {
    color: #fff;
    --swiper-navigation-size: 32px;

    @media (max-width: 768px) {
      display: none;
    }
  }
}

.lightbox-thumbs-swiper {
  width: 100%;
  max-height: 80px;
  margin-top: 12px;
  flex-shrink: 0;

  :deep(.swiper-wrapper) {
    justify-content: center;
  }

  :deep(.swiper-slide) {
    width: 72px;
    height: 72px;
    opacity: 0.5;
    cursor: pointer;
    transition: opacity 0.2s;
    flex-shrink: 0;
  }

  :deep(.swiper-slide-thumb-active) {
    opacity: 1;
    border: 2px solid #fff;
    border-radius: 4px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }
}
</style>
